import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { apiService } from '@/service/api'
import { useTranslation } from 'react-i18next'
import { ReviewTab } from '@/components/DocumentViewer/ReviewTab'

/**
 * Read-only preview of a FS-557 "User Artifact" — a `type='transcript'`
 * document whose body is markdown stored as the file itself.
 *
 * UI-03: every other document type had a preview on the board; this one fell
 * through the switch to `null`, so a user artifact rendered as an empty card
 * and its "open" went to the data catalogue instead of the artifact. The
 * catalogue's own preview modal was worse — it reported "Document not found"
 * for a file that exists.
 *
 * The body comes from `downloadDocument`, the same call the full viewer at
 * `/user-artifact/:doc_id` makes; there is no metadata endpoint for the text.
 * `ReviewTab` renders it, so the preview and the viewer cannot drift in how
 * they display the same markdown — and it is read-only here because editing is
 * driven by an `isEditing` prop this deliberately does not pass.
 */
export default function UserArtifactPreview({ docId }: { docId: string }) {
  const { t } = useTranslation()
  const [content, setContent] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setContent(null)
    setFailed(false)
    apiService
      .downloadDocument(docId)
      .then((blob) => blob.text())
      .then((text) => {
        // A card can be swapped before the fetch lands; without this the
        // previous artifact's body paints over the new one.
        if (!cancelled) setContent(text)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [docId])

  if (failed) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
        {t('board:document_preview.preview_not_available', {
          defaultValue: 'Document preview not available',
        })}
      </div>
    )
  }

  if (content === null) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  return <ReviewTab pageText={content} />
}
